import { useGetEmployeesMyWallet } from '@agos/api-client/hr'
import { OrderNumberLink, T, alsoDrivesOf, formatCents, formatCount, tt, useAuth } from '@agos/shared'
import { Spinner, cn, BUTTON_PRIMARY} from '@agos/ui'
import { ArrowLeft, Calculator, EyeOff, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'wouter'

import { HandoverSheet } from './HandoverSheet'
import {
  cashNote,
  dayLabel,
  daysOf,
  dayTotals,
  hiddenNote,
  lifetimeNote,
  methodLabel,
  documentedCentsOf,
  documentedSplitOf,
  externalSettleNote,
  ordersOnDay,
  outstandingNote,
  ownerOf,
  payNote,
  payOf,
  pillsOf,
  statusNote,
  todayKey,
  walletOrdersOf,
  withheldNote,
  type WalletOrder,
} from './wallet'

const DRIVES = new Set(['driver', 'main_admin', 'admin'])

/**
 * The driver's wallet — the port of the retired Expo app's second tab.
 *
 * Phone-first and fullscreen, like `/driving`. What it does **not** do is print the server's `cardTotal`
 * next to its `cashTotal`: that field leaves out `card_courier`, which for the driver in this data is
 * twice its size. See `wallet.ts` for FB-185 in full.
 *
 * Open to a waiter too, because `my-wallet` is: the join is `employee_id` for anyone who is not a driver,
 * and 2 813 orders worth 48 504,22 € sit on that column, all of them waiters'.
 */
export function WalletPage() {
  const walletQuery = useGetEmployeesMyWallet()
  /* §9.804, owner O151 - *"ukazovať prvé dnešný deň, nie Všetko"*: the screen opens on the device's today. */
  const [day, setDay] = useState<string | null>(() => todayKey(new Date()))
  const [calculating, setCalculating] = useState(false)
  const { hasPermission, user } = useAuth()

  // Back to wherever this person came from. A waiter has a wallet too — 2 813 orders join on
  // `employee_id` — and `/driving` would refuse them, so the arrow must not point there.
  // §9.686, owner #25 - an account the main admin marked *Jazdí aj rozvoz* came from the driving app too.
  const backHref = DRIVES.has(user?.systemRole ?? '') || alsoDrivesOf(user) ? '/driving' : '/dashboard'

  const owner = useMemo(() => ownerOf(walletQuery.data), [walletQuery.data])
  const orders = useMemo(() => walletOrdersOf(walletQuery.data), [walletQuery.data])
  const days = useMemo(() => daysOf(orders), [orders])
  const shown = useMemo(() => ordersOnDay(orders, day), [orders, day])
  const totals = useMemo(() => dayTotals(shown), [shown])
  const documentedCents = useMemo(() => documentedCentsOf(shown), [shown])
  const fiskalSplit = useMemo(() => documentedSplitOf(shown), [shown])
  const maySeeFiskal = hasPermission('fiskal') || hasPermission('fiskal_reconcile')
  // §9.804, owner O151 - an external driver's pay for the shown orders, by the server's rule (§10A.450).
  const pay = useMemo(() => payOf(shown), [shown])

  // Read once per render rather than at module load, so a tab open across midnight relabels (FB-171).
  const today = new Date()

  return (
    <div className="min-h-dvh bg-background text-foreground pb-8">
      <header className="safe-pt-3 sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 pb-3 flex items-center gap-3">
        <Link
          href={backHref}
          className="min-h-[44px] min-w-[44px] -ml-2 flex items-center justify-center rounded-xl hover:bg-muted"
          aria-label={tt('Späť')}
          data-back
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold truncate flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            <T>Peňaženka</T>
          </h1>
          {owner !== null && (
            <p className="text-xs text-muted-foreground truncate" data-owner>
              {owner.name}
              {owner.position !== null && ` · ${owner.position}`}
            </p>
          )}
        </div>
      </header>

      {walletQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : owner === null ? (
        <p className="px-4 py-16 text-center text-sm text-muted-foreground" data-wallet-error>
          <T>Peňaženku sa nepodarilo načítať.</T>
        </p>
      ) : !owner.showWallet ? (
        /* The server sends every order regardless — hiding is this screen's job, and saying why is what
         * stops a driver assuming the app is broken. */
        <div className="px-4 py-16 text-center space-y-3" data-wallet-hidden>
          <EyeOff className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm font-semibold">
            <T>Peňaženka je skrytá</T>
          </p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">{hiddenNote()}</p>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* §9.804, owner O151 - today first and always, the state the screen opens in; then `Všetko`. */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-day-pills>
            {pillsOf(days, todayKey(today)).map((pill) =>
              pill.kind === 'all' ? (
                <DayPill key="all" active={day === null} onClick={() => setDay(null)} label={tt('Všetko')} />
              ) : (
                <DayPill
                  key={pill.day.key}
                  active={day === pill.day.key}
                  onClick={() => setDay(pill.day.key)}
                  label={dayLabel(pill.day.key, today)}
                  amount={formatCents(pill.day.totalCents)}
                  dayKey={pill.day.key}
                />
              ),
            )}
          </div>

          {/* The cash in the bag. Not the day total, and not cash-plus-card: a payment taken on the
            * driver's own reader settled to the restaurant and is not theirs to hand over. */}
          <section className="rounded-2xl bg-card border border-border p-4 space-y-3" data-handover>
            <p className="text-2xl font-bold tabular-nums" data-handover-amount>
              {formatCents(totals.cashCents)}
            </p>
            <p className="text-[11px] text-muted-foreground" data-handover-note>
              {cashNote(totals)}
            </p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1 text-xs">
              <Split label={methodLabel('cash')} cents={totals.cashCents} slug="cash" />
              <Split label={methodLabel('card_courier')} cents={totals.courierCents} slug="courier" />
              <Split label={methodLabel('card_terminal')} cents={totals.terminalCents} slug="terminal" />
              <Split label={methodLabel('card_online')} cents={totals.onlineCents} slug="online" />
              {/* §9.767, owner O129 - *"also modals for them (čašník & rozvozár app)"*: QR Pay always, the line
                * after CardPay online, 0,00 € included. Counted in *Spolu* below either way. */}
              <Split label={methodLabel('qr_pay')} cents={totals.qrPayCents} slug="qr" />
            </dl>
            <p className="text-[11px] text-muted-foreground pt-1" data-day-total>
              {tt('Spolu')} {formatCents(totals.totalCents)} {tt('z')} {formatCount(totals.count)}{' '}
              {tt('objednávok')}
            </p>
            {/* §9.804, owner O151 - *"nevidí koľko má dostať peňazí"*: an external driver's pay, and how the cash
              * splits between him and the restaurant, the netting §10A.438 E settles at pickup. */}
            {owner.isExternal && (
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 space-y-1" data-courier-pay>
                <p className="text-xs text-muted-foreground">
                  <T>Váš zárobok</T>
                </p>
                <p className="text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400" data-courier-pay-amount>
                  {formatCents(pay.rewardCents)}
                </p>
                <p className="text-sm font-semibold" data-courier-pay-split>
                  {payNote(totals.cashCents, pay.rewardCents)}
                </p>
              </div>
            )}
            {/* §9.568, FB-863 - the part of this the register has a live document for, because the
              * uzávierka counts only that subset and this sheet could not say which part was which.
              * It does not make the two equal: §9.449's scope difference stands. */}
            {maySeeFiskal && (
            <p
              className="text-[11px] font-semibold text-sky-700 dark:text-sky-300"
              data-day-documented={String(documentedCents)}
            >
              {documentedCents === totals.totalCents
                ? `${tt('Celá suma má fiškálny doklad.')}`
                : `${tt('S fiškálnym dokladom')} ${formatCents(documentedCents)} - ${tt('bez dokladu')} ${formatCents(totals.totalCents - documentedCents)}. ${tt('Uzávierka počíta len sumy s dokladom.')}`}
            </p>
            )}
            {/* §9.571 - the owner's instruction, behind `fiskal_reconcile` so not everyone sees it. */}
            {maySeeFiskal && (
              <div className="flex items-center gap-3 text-[11px] font-semibold pt-0.5" data-day-fiskal-split>
                <span className="text-emerald-700 dark:text-emerald-400" data-fiskal-cash={String(fiskalSplit.cashCents)}>
                  <T>Fiskal Hotovosť</T> {formatCents(fiskalSplit.cashCents)}
                </span>
                <span className="text-sky-700 dark:text-sky-300" data-fiskal-card={String(fiskalSplit.cardCents)}>
                  <T>Fiskal Karta</T> {formatCents(fiskalSplit.cardCents)}
                </span>
              </div>
            )}
          </section>

          {/* The calculator settles a day, so it needs one. Without `forDate` the server assumes today
            * and a shift settled after midnight would land on the wrong day's orders — so rather than
            * opening on `Všetko` and guessing, the button says what it needs. */}
          {owner.isExternal ? (
            /* §9.804 - the server refuses an external driver's handover (§10A.450), so no form that can only fail. */
            <p className="text-[11px] text-muted-foreground px-1" data-courier-settles>
              {externalSettleNote()}
            </p>
          ) : (
          <button
            type="button"
            data-open-calculator
            disabled={day === null}
            onClick={() => setCalculating(true)}
            className={cn('w-full min-h-[48px] rounded-2xl', BUTTON_PRIMARY, 'flex items-center justify-center gap-2')}
          >
            <Calculator className="h-4 w-4" />
            <T>Kalkulačka tržby</T>
          </button>
          )}
          {!owner.isExternal && day === null && (
            <p className="text-[11px] text-muted-foreground -mt-2 px-1" data-calculator-needs-day>
              <T>Vyberte deň, ktorý odovzdávate - tržba sa zapisuje na konkrétny deň.</T>
            </p>
          )}

          {withheldNote(totals) !== '' && (
            <p className="text-[11px] text-amber-400/90 px-1" data-withheld>
              {withheldNote(totals)}
            </p>
          )}
          {outstandingNote(totals) !== '' && (
            <p className="text-[11px] text-muted-foreground px-1" data-outstanding>
              {outstandingNote(totals)}
            </p>
          )}

          <div className="space-y-2">
            {shown.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground" data-wallet-empty>
                <T>Za tento deň tu nič nie je.</T>
              </p>
            ) : (
              shown.map((order) => <Row key={order.id} order={order} />)
            )}
          </div>

          <p className="text-[11px] text-muted-foreground pt-2 px-1" data-lifetime>
            {lifetimeNote(owner)}
          </p>
        </div>
      )}

      {calculating && day !== null && (
        <HandoverSheet
          totals={totals}
          day={day}
          dayName={dayLabel(day, today)}
          systemRole={user?.systemRole}
          alsoDrives={alsoDrivesOf(user)}
          onClose={() => setCalculating(false)}
        />
      )}
    </div>
  )
}

function DayPill(props: {
  active: boolean
  onClick: () => void
  label: string
  amount?: string
  dayKey?: string
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      data-day-pill={props.dayKey ?? 'all'}
      aria-pressed={props.active}
      className={cn(
        'shrink-0 min-h-[44px] rounded-xl px-3 text-xs font-semibold border transition-colors',
        props.active
          ? 'bg-primary text-primary-foreground border-transparent'
          : 'bg-card border-border text-muted-foreground',
      )}
    >
      <span className="block">{props.label}</span>
      {props.amount !== undefined && (
        <span className="block text-[10px] font-normal tabular-nums opacity-80">{props.amount}</span>
      )}
    </button>
  )
}

function Split(props: { label: string; cents: number; slug: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{props.label}</dt>
      <dd className="font-semibold tabular-nums" data-split={props.slug}>
        {formatCents(props.cents)}
      </dd>
    </div>
  )
}

function Row({ order }: { order: WalletOrder }) {
  const note = statusNote(order)
  return (
    <article
      className="rounded-2xl bg-card border border-border p-3 flex items-start gap-3"
      data-wallet-order={order.id}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">
          {/* §9.143 - a wallet is a ledger. The link is drawn only for an employee holding
            * `lists`, which most drivers do not, so for them this stays exactly as it was. */}
          <OrderNumberLink
            orderId={order.id}
            className="text-primary hover:underline"
            title={`${tt('Otvoriť v archíve')}: #${order.orderNumber ?? ''}`}
          >
            #{order.orderNumber}
          </OrderNumberLink>
          {order.customerName !== null && ` · ${order.customerName}`}
        </p>
        {order.customerAddress !== null && (
          <p className="text-xs text-muted-foreground truncate">{order.customerAddress}</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          {methodLabel(order.paymentMethod)}
          {/* §9.804, owner O151 - an external driver's pay for this order; nobody else's orders carry one. */}
          {order.rewardCents !== null && (
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold" data-order-reward={order.rewardCents}>
              {' '}
              · {tt('odmena')} {formatCents(order.rewardCents)}
            </span>
          )}
          {note !== '' && (
            <span className="text-amber-400/90" data-order-outstanding>
              {' '}
              · {note}
            </span>
          )}
        </p>
      </div>
      <p className="text-sm font-bold tabular-nums shrink-0">{formatCents(order.totalCents)}</p>
    </article>
  )
}
